/**
 * useRegionCoverageData.js — 방문빈도 화면의 불러오기(관리 권한·색상 기준·방문 집계)
 *
 * RegionCoverage.jsx(288줄)에서 떼어냄(2026-10-29, 300줄 규칙 — 글자 그대로 옮김, 동작 변화 없음).
 *
 * 2026-10-29 분류 버튼·통합 메뉴:
 *   - 서버가 방문 집계와 함께 분류 버튼(categories — 보는 회사들의 장부 분류, 다나가 순)과 이번에 쓴 분류(category, '' = 전체)를 준다.
 *     처음엔 분류를 안 보내 서버가 내 회사 분류로 고른다. 버튼을 누르면 그 분류('' 는 '*' = 전체)로 다시 부른다.
 *   - 통합 메뉴 '방문빈도 관리'는 같은 화면을 주소만 바꿔 쓴다(coverageUrl·rulesUrl) — 설정 열쇠는 manageKeys.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { DEFAULT_PERIOD, DEFAULT_COLOR_RULES } from './helpers';
import { isAdminUser } from '../adminlayout/sidebarFilters';

const COVERAGE_URL = '/api/admin/regions/coverage';
const RULES_URL = '/api/admin/settings/region-color-rules';
const MANAGE_KEYS = ['REGION_COVERAGE_MANAGEMENT'];

export function useRegionCoverageData({
  adminMode = false, coverageUrl = COVERAGE_URL, rulesUrl = RULES_URL, manageKeys = MANAGE_KEYS,
} = {}) {
  const { showToast } = useToast();
  // ⚙️ 설정(지역 색상 기준) — 관리자 주소 + 방문빈도 관리 권한일 때만 보인다(fail-closed).
  const [canManage, setCanManage] = useState(false);

  // ── 상태 ────────────────────────────────────────────────────────────────────
  const [regions,     setRegions]    = useState([]);
  const [loading,     setLoading]    = useState(false);
  const [colorRules,  setColorRules] = useState(DEFAULT_COLOR_RULES);
  // 분류 버튼 — category: null = 아직 모름(서버 답을 기다림), '' = 전체
  const [categories,  setCategories] = useState([]);
  const [category,    setCategory]   = useState(null);
  const requestSeq = useRef(0); // 버튼을 빨리 눌러도 마지막 요청 답만 쓴다
  const manageKeyList = manageKeys.join('|');

  useEffect(() => {
    if (!adminMode) return undefined;
    let active = true;
    // 운영자(role=admin)는 항상, 그 외에는 '방문빈도 관리' 배정자만.
    //   미팅링크에는 그 권한 코드 자체가 없어(파인그룹에만 있음) 사실상 운영자 전용이 된다.
    if (isAdminUser()) { setCanManage(true); return undefined; }
    apiClient.get('/api/my/permissions').then((r) => {
      const codes = r?.ok && Array.isArray(r.data?.permCodes) ? r.data.permCodes : [];
      if (active) setCanManage(manageKeyList.split('|').some((k) => codes.includes(k)));
    }).catch(() => {});
    return () => { active = false; };
  }, [adminMode, manageKeyList]);

  // ── 데이터 조회 ─────────────────────────────────────────────────────────────

  /** 색상 기준 규칙 조회 (TicketTypeManagement에서 동적 변경 가능) */
  const fetchColorRules = useCallback(async () => {
    const result = await apiClient.get(rulesUrl);
    if (result.ok && result.data?.data) {
      setColorRules((prev) => ({ ...prev, ...result.data.data }));
    }
  }, [rulesUrl]);

  /** 지역별 방문 집계 데이터 조회 — requested: 안 줌 = 서버가 내 회사 분류로 / '' = 전체 / 분류 이름 */
  const fetchRegions = useCallback(async (requested) => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    const q = requested === undefined ? '' : `&category=${encodeURIComponent(requested === '' ? '*' : requested)}`;
    const result = await apiClient.get(`${coverageUrl}?days=${DEFAULT_PERIOD}${q}`);
    if (seq !== requestSeq.current) return;
    setLoading(false);
    if (!result.ok) {
      showToast(result.data?.message || '지역 데이터 조회 실패', 'error');
      return;
    }
    setRegions(Array.isArray(result.data?.data) ? result.data.data : []);
    setCategories(Array.isArray(result.data?.categories) ? result.data.categories : []);
    setCategory(typeof result.data?.category === 'string' ? result.data.category : '');
  }, [showToast, coverageUrl]);

  useEffect(() => {
    // 색상 기준과 지역 데이터를 병렬 조회
    fetchColorRules();
    fetchRegions();
  }, [fetchColorRules, fetchRegions]);

  /** 분류 버튼 누름 — 바로 눌린 모양으로 바꾸고 그 분류로 다시 부른다 */
  const selectCategory = useCallback((name) => {
    setCategory(name);
    fetchRegions(name);
  }, [fetchRegions]);

  return { canManage, regions, loading, colorRules, fetchColorRules, categories, category, selectCategory };
}

export default useRegionCoverageData;
